import { faCompress, faExpand, faRepeat } from "@fortawesome/pro-regular-svg-icons";
import { faPlay, faPause, faBackwardStep, faForwardStep } from "@fortawesome/pro-solid-svg-icons";
import { Box, Fade, Typography, Theme } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { SxProps } from "@mui/material/styles";
import { useLocalStorageValue } from "@react-hookz/web";
import { ReactElement, type JSX } from "react";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import Seeker, { formatVideoTime } from "../Seeker/Seeker";
import { MarkType } from "../Seeker/type";
import { IconButton } from "./IconButton";
import PlaybackRateMenu from "./PlaybackRateMenu";
import VolumeControl from "./VolumeButton";

type MediaControlProps = {
  disabled?: boolean;
  duration: number;
  framesPerSecond: number;
  fullscreenMode?: boolean;
  compactMode?: boolean;
  marks?: MarkType[];
  onInteractFullscreen?: () => void;
  onClickFullscreen?: (next: boolean) => void;
  onClickMark?: (mark: MarkType) => void;
  onPlaybackRateChange?: (rate: number) => void;
  onSeek: (n: number) => void;
  onToggleLoop?: () => void;
  onTogglePlay: (playing: boolean) => void;
  onToggleSound?: (on: boolean) => void;
  onVolumeChange?: (volume: number) => void;
  OverlayToggle?: ReactElement;
  playing: boolean;
  progress: number;
  showFullscreenControl?: boolean;
  showRewindControls?: boolean;
  sx?: SxProps<Theme>;
};

export const MediaControl = ({
  disabled,
  duration,
  framesPerSecond,
  fullscreenMode,
  compactMode,
  marks,
  onClickFullscreen,
  onClickMark,
  onInteractFullscreen,
  onSeek,
  onTogglePlay,
  OverlayToggle,
  playing,
  progress,
  showFullscreenControl,
  showRewindControls,
  sx,
}: MediaControlProps): JSX.Element | null => {
  const DEFAULT_FRAMES_PER_SECOND = 25;

  const { value: playerLoop, set: setPlayerLoop } = useLocalStorageValue("player-loop", {
    defaultValue: false,
    initializeWithValue: true,
  });
  const { value: playerMuted, set: setPlayerMuted } = useLocalStorageValue("player-muted", {
    defaultValue: true,
    initializeWithValue: true,
  });
  const { value: playerVolume, set: setPlayerVolume } = useLocalStorageValue("player-volume", {
    defaultValue: 1,
    initializeWithValue: true,
  });
  const { value: playbackRate, set: setPlaybackRate } = useLocalStorageValue("playback-rate", {
    defaultValue: 1,
    initializeWithValue: true,
  });

  const handleLoopToggle = () => {
    setPlayerLoop(!playerLoop);
  };

  const handleSoundToggle = () => {
    setPlayerMuted(!playerMuted);
  };

  const handlePlayerVolume = (volume: number) => {
    setPlayerVolume(volume);
  };

  const handlePlaybackRate = (rate: number) => {
    setPlaybackRate(rate);
  };

  const handleRewind = (seek: number) => {
    onTogglePlay(false);
    onSeek(seek);
  };

  if (fullscreenMode) {
    return disabled ? null : (
      <Fade in={showFullscreenControl} timeout={600}>
        <Grid
          sx={[
            {
              position: "fixed",
              zIndex: 10001,
              width: "100vw",
              left: 0,
              bottom: 0,
              p: 2,
              button: { opacity: 1 },
              backdropFilter: "blur(16px)",
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          onTouchMove={onInteractFullscreen}
          onClick={onInteractFullscreen}
          aria-label="fullscreen control"
          className="MuiMediaControl-container"
        >
          <Seeker
            duration={duration || 0}
            framesPerSecond={framesPerSecond || DEFAULT_FRAMES_PER_SECOND}
            onSeek={onSeek}
            progress={progress}
            marks={marks}
            sx={[{ mb: 0.5 }, ...(Array.isArray(sx) ? sx : [sx])]}
          />
          <Grid container justifyContent="space-between">
            <Typography variant="h6" component="div">
              {formatVideoTime(progress)}
            </Typography>
            <Typography variant="h6" component="div" color="text.secondary">
              {formatVideoTime(duration || 0)}
            </Typography>
          </Grid>
          <Grid container alignItems="center">
            <Grid item container xs justifyContent="space-between" alignItems="center">
              <Grid item xs>
                {OverlayToggle}
              </Grid>
              <Grid item container xs md={3} justifyContent="flex-end">
                <PlaybackRateMenu value={playbackRate} onChange={handlePlaybackRate} plain />
              </Grid>
            </Grid>

            <Grid item container xs md={6} justifyContent="center">
              {/* Rewind Button */}
              {showRewindControls && (
                <IconButton
                  aria-label="rewind"
                  onClick={() => handleRewind(0)}
                  disabled={disabled}
                  size="large"
                >
                  <FaIcon icon={faBackwardStep} />
                </IconButton>
              )}

              {/* Play Button */}
              <IconButton
                aria-label={playing ? "pause" : "play"}
                onClick={() => onTogglePlay(!playing)}
                disabled={disabled}
                size="large"
              >
                <FaIcon icon={playing ? faPause : faPlay} />
              </IconButton>

              {/* Fast Forward Button */}
              {showRewindControls && (
                <IconButton
                  aria-label="fast forward"
                  onClick={() => handleRewind(duration || 0)}
                  disabled={disabled}
                  size="large"
                >
                  <FaIcon icon={faForwardStep} />
                </IconButton>
              )}
            </Grid>

            <Grid item container xs md={3} justifyContent="space-between">
              {/* Loop Toggle */}
              <IconButton
                aria-label="toggle loop"
                onClick={() => setPlayerLoop(!playing)}
                color={playerLoop ? "primary" : "default"}
              >
                <FaIcon icon={faRepeat} />
              </IconButton>
              {/* Fullscreen Button */}
              <IconButton
                aria-label="fullscreen"
                onClick={() => (onClickFullscreen ? onClickFullscreen(false) : {})}
              >
                <FaIcon icon={faCompress} />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </Fade>
    );
  }

  return (
    <Box
      sx={[
        {
          position: compactMode ? "absolute" : "relative",
          zIndex: 10,
          width: "100%",
          bottom: 0,
          background: (theme) =>
            compactMode ? theme.palette.gradient.blackToTransparent : "transparent",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Seeker
        duration={duration || 0}
        framesPerSecond={framesPerSecond || DEFAULT_FRAMES_PER_SECOND}
        marks={marks}
        marksDisabled={disabled}
        onClickMark={onClickMark}
        onSeek={onSeek}
        progress={progress}
        seekDisabled={disabled}
        sx={{ display: { xs: "none", sm: "block" }, mx: 2, mb: 0 }}
      />
      <Grid
        container
        alignItems="center"
        sx={{
          display: { xs: "none", sm: "flex" },
          px: 2,
          pb: 2,
        }}
      >
        <Grid
          container
          item
          xs
          justifyContent="flex-start"
          alignItems="center"
          gap={2}
          sx={{ flexWrap: "nowrap" }}
        >
          {/* Rewind Button */}
          {showRewindControls && !compactMode && (
            <IconButton aria-label="rewind" onClick={() => handleRewind(0)} disabled={disabled}>
              <FaIcon icon={faBackwardStep} />
            </IconButton>
          )}

          {/* Play Button */}
          <IconButton
            aria-label={playing ? "pause" : "play"}
            onClick={() => onTogglePlay(!playing)}
            disabled={disabled}
          >
            <FaIcon icon={playing ? faPause : faPlay} />
          </IconButton>

          {/* Fast Forward Button */}
          {showRewindControls && !compactMode && (
            <IconButton
              aria-label="fast forward"
              onClick={() => handleRewind(duration || 0)}
              disabled={disabled}
            >
              <FaIcon icon={faForwardStep} />
            </IconButton>
          )}

          {/* Playback Menu */}
          {!compactMode && <PlaybackRateMenu value={playbackRate} onChange={handlePlaybackRate} />}
          {/* Volume */}
          <VolumeControl
            muted={playerMuted}
            onChange={(volume) => handlePlayerVolume(volume)}
            onToggle={handleSoundToggle}
            volume={playerVolume}
            compactMode={compactMode}
          />
        </Grid>

        <Grid item justifyContent="center">
          <Typography variant="h5" component="span" color="text.primary">
            {formatVideoTime(progress)}
            <Box color="text.secondary" component="span">
              {" / "}
              {formatVideoTime(duration || 0)}
            </Box>
          </Typography>
        </Grid>

        <Grid container item xs justifyContent="flex-end">
          {OverlayToggle}
          {/* Loop Toggle */}
          {!compactMode && (
            <IconButton
              aria-label="toggle loop"
              onClick={handleLoopToggle}
              color={playerLoop ? "primary" : "default"}
            >
              <FaIcon icon={faRepeat} />
            </IconButton>
          )}
          {/* Fullscreen Button */}
          {showFullscreenControl ? (
            <IconButton
              aria-label="fullscreen"
              onClick={() => (onClickFullscreen ? onClickFullscreen(true) : {})}
              sx={{ ml: 2 }}
            >
              <FaIcon icon={faExpand} />
            </IconButton>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};
